import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
  computed,
  inject,
  output,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  Subject,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import { ICONS } from '../../../core/services/icons/icons.service';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { SpinnerComponent } from '../spinner/spinner.component';

@Component({
  selector: 'app-search-pop',
  imports: [ClickOutsideDirective, ReactiveFormsModule, SpinnerComponent],
  templateUrl: './search-pop.component.html',
  styles: ``
})
export class SearchPopComponent implements AfterViewInit, OnDestroy {
  private translate = inject(TranslateService);
  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );
  closePopup = output();
  searchIcon = ICONS.magnifyingGlass;
  searchControl = new FormControl('');
  searchPlaceholder = computed(() => {
    this.currentLang();
    return this.translate.instant('search_popup.placeholder');
  });

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();

  ngAfterViewInit() {
    this.searchInput.nativeElement.focus();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Escape':
        this.closePopup.emit();
        break;
      case 'ArrowDown':
        // Navigate to first result
        event.preventDefault();
        break;
      case 'Enter':
        // Select highlighted result
        event.preventDefault();
        break;
    }
  }

  constructor() {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) => this.queryBackend(value)),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        console.log('Search result:', result);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isLoading = signal(false);
  searchResults = signal<{ id?: number; title: string }[]>([]);

  queryBackend(query: string | null) {
    if (!query) {
      this.searchResults.set([]);
      return of({ result: '' });
    }

    this.isLoading.set(true);
    // Simulate API call
    return of({ result: `Results for "${query}"` }).pipe(
      delay(500), // Simulate network delay
      tap(() => {
        if (typeof this.searchControl.value == 'string') {
          if (this.searchControl.value.includes('vehicle')) {
            this.searchResults.set([
              {
                title: 'vehicle v245'
              },
              {
                title: 'vehicle v248'
              },
              {
                title: 'vehicle v247'
              }
            ]);
          }
        }
        this.isLoading.set(false);
      }),
      tap(() => this.isLoading.set(false))
    );
  }
}
